import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Link, Typography } from '@mui/material';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { Elabels, licencesTableColumns, mapLicencesToDataSource } from '../../helpers';
import { LicencesTableCard } from './LicencesTableCard';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useParams } from 'react-router-dom';
import { useCase } from '../../hooks';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ISortOptions } from 'models/table';
import { Loader } from 'components/atoms/Loader';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { FormModal } from 'components/molecules/Modal';
import { ERouteLinks } from 'models/route';
import { colors } from 'theme/colors';

const Licences = () => {
  const { t } = useTranslations();
  const { id = '', caseId = '' } = useParams<{ id: string; caseId: string }>();
  const { shortDateFormat, getProperties } = useGlobalProperty();
  const { isDesktop } = useThemeBreakpoint();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const { getLicencesData, licences, paginatorLicences } = useCase();
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [modalAssetsData, setModalAssetsData] = useState<any>();
  const { selectedLanguage } = useLang();
  const { weaponLicenceStatus, getWeaponLicenceStatus } = useDictionary();

  useEffect(() => {
    getProperties();
  }, []);
  useEffect(() => {
    if (!weaponLicenceStatus) {
      getWeaponLicenceStatus();
    }
  }, [selectedLanguage]);

  useEffect(() => {
    (async () => {
      setIsPaginationLoading(true);
      if (!licences.length) {
        setIsLoading(true);
      }
      await getLicencesData(caseId, currentPage, perPage, sort);
      setIsLoading(false);
      setIsPaginationLoading(false);
    })();
  }, [currentPage, perPage, sort]);

  return (
    <Grid sx={{ gap: '20px' }}>
      <FormModal
        open={!!modalAssetsData}
        onClickClose={() => setModalAssetsData(null)}
        approveButtonLabel="general.ok.label"
        title={t(Elabels.assetsList)}
        titleLeft
        cancelButtonLabel={t('general.close.label')}
      >
        <Grid
          container
          style={{
            background: colors.gray100,
            border: `1px solid ${colors.gray200}`,
            borderRadius: '4px',
            padding: '20px',
          }}
        >
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t(Elabels.assetsId)}
          </Typography>
          {modalAssetsData &&
            modalAssetsData.length > 1 &&
            modalAssetsData.map((item: any) => (
              <Grid size={{ xs: 12 }}>
                <Link
                  href={ERouteLinks.ViewRegisteredFirearm.replace(':id', item.uniqueId)}
                  underline="none"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.fireCycleId}
                </Link>
              </Grid>
            ))}
        </Grid>
      </FormModal>
      <Grid
        container
        columnSpacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          rowGap: 1,
          flexWrap: 'nowrap',
          pl: 1,
          mb: 2,
        }}
      >
        <Typography variant="body2">{t(Elabels.licencesTabText)}</Typography>
      </Grid>

      {isLoading ? (
        <Grid size={{ xs: 12 }}>
          <Loader isLoading />
        </Grid>
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={licencesTableColumns({ t, caseId, setModalAssetsData })}
              dataSource={mapLicencesToDataSource(
                licences,
                shortDateFormat,
                weaponLicenceStatus,
              )}
              onSort={setSort}
              id="licences_list-table"
            />
          ) : (
            <CardList
              items={mapLicencesToDataSource(licences, shortDateFormat, weaponLicenceStatus)}
              render={(data, index) => (
                <LicencesTableCard
                  data={data}
                  key={index}
                  caseId={caseId}
                  setModalAssetsData={setModalAssetsData}
                />
              )}
            />
          )}
          {paginatorLicences && (
            <Pagination
              count={paginatorLicences.totalElements}
              onChangePage={(page) => setCurrentPage(page)}
              current={currentPage}
              perPage={perPage}
              onChangePerPage={(value) => setPerPage(value)}
              isLoading={isPaginationLoading}
            />
          )}
        </>
      )}
    </Grid>
  );
};

export default Licences;
